import { FunctionComponent } from "react";
import { useTranslation } from "react-i18next";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";

import { CATALOG_EXAMPLES } from "../config/catalogExamples";

interface AiSearchBarProps {
	query: string;
	/** The example chip currently narrowing the catalog, or `null` when plain search is active. */
	activeExampleKey: string | null;
	onQueryChange: (query: string) => void;
	onSelectExample: (key: string) => void;
}

export const AiSearchBar: FunctionComponent<AiSearchBarProps> = ({
	query,
	activeExampleKey,
	onQueryChange,
	onSelectExample,
}) => {
	const { t } = useTranslation();

	return (
		<div>
			<div className="relative overflow-hidden rounded-[14px] border border-border-light bg-white p-4 focus-within:border-blue-action focus-within:shadow-sm">
				<div className="flex items-center gap-3">
					<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background-light text-text-meta">
						<Icon className="h-4 w-4" icon={faMagnifyingGlass} />
					</div>
					<input
						className="min-w-0 flex-1 bg-transparent py-2 text-sm text-text outline-none placeholder:text-text-disabled"
						placeholder={t("list.create.catalog.ai.placeholder")}
						type="text"
						value={query}
						onChange={(event) => onQueryChange(event.target.value)}
					/>
				</div>
			</div>

			<div className="mt-3 flex flex-wrap items-center gap-2">
				<span className="text-xs font-medium text-text-meta">
					{t("list.create.catalog.ai.examplesLabel")}
				</span>
				{CATALOG_EXAMPLES.map(({ key }) => {
					const isActive = activeExampleKey === key;

					return (
						<button
							key={key}
							aria-pressed={isActive}
							className={classNames(
								"cursor-pointer rounded-[20px] border px-3.5 py-1.5 text-xs transition",
								isActive
									? "border-action-400 bg-blue-action-200 text-blue-action"
									: "border-border-light bg-white text-text-meta hover:border-action-400 hover:bg-blue-action-200 hover:text-blue-action"
							)}
							type="button"
							onClick={() => onSelectExample(key)}
						>
							{t(`list.create.catalog.ai.examples.${key}.label`)}
						</button>
					);
				})}
			</div>
		</div>
	);
};
